import type { CSSProperties } from 'react';
import styled, { css } from 'styled-components';

import { InteractiveElement } from '#src/atoms/animations/InteractiveElement/index.tsx';
import { ResponsiveImg } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { useTouchScreen } from '#src/hooks/device/useTouchScreen.ts';

const Root = styled.div`
  isolation: isolate;
  position: relative;
  height: 100%;
`;

const AnimatedBlock = styled.div`
  height: 100%;
  transform-style: preserve-3d;
  transform: perspective(600px)
    rotateX(calc((var(--my) - 0.5) * 100 * var(--wiggle)))
    rotateY(calc((0.5 - var(--mx)) * 100 * var(--wiggle)));
`;

const Glare = styled.div`
  display: block;
  position: absolute;
  z-index: 1;
  inset: 0;
  pointer-events: none;
  transform-style: preserve-3d;
  mix-blend-mode: overlay;
  opacity: calc(var(--active) * 0.4);
  mask-size: contain;
`;

const imgStyle = css`
  width: 100%;
  transform-style: preserve-3d;
  will-change: transform;
`;
const ResponsiveCardImage = styled(ResponsiveImg)`
  ${imgStyle}
`;
const CardImage = styled.img`
  ${imgStyle}
`;

type Props = {
  pictureUrl: string | null;
  width?: number;
  wigglePower?: number;
  alt?: string;
  className?: string;
  disabled?: boolean;
  idle?: boolean;
  children?: React.ReactNode;
};

export const GlareEffect = ({
  pictureUrl,
  width,
  wigglePower = 0.07,
  alt = '',
  className,
  disabled = false,
  idle: idleProp,
  children,
}: Props) => {
  const isTouchScreen = useTouchScreen();

  if (!pictureUrl) {
    return null;
  }

  const idle = idleProp ?? isTouchScreen;

  const glareBackground = `radial-gradient(
    farthest-corner circle at calc(var(--mx) * 100%) calc(var(--my) * 100%),
    rgba(255, 255, 255, 1) 10%,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0) 100%
  ), url('${pictureUrl}') center / cover`;
  const glareMask = `url('${pictureUrl}')`;

  return (
    <Root
      className={className}
      style={{ '--wiggle': `${wigglePower}deg` } as CSSProperties}
    >
      <InteractiveElement idle={idle} disabled={disabled}>
        <AnimatedBlock>
          {children ??
            (width ? (
              <ResponsiveCardImage src={pictureUrl} alt={alt} width={width} />
            ) : (
              <CardImage src={pictureUrl} alt={alt} />
            ))}

          <Glare
            style={{ background: glareBackground, maskImage: glareMask }}
          />
        </AnimatedBlock>
      </InteractiveElement>
    </Root>
  );
};
